import type { ReactNode } from 'react';
import type {
  ItineraryDetailSearch,
  ItineraryView,
} from '../model/itinerary-view';
import { useChapterEditorStore } from '@itineraries/widgets/itinerary-chapters';
// Deep import (not the widget barrel) so the tabs don't transitively pull
// `ItineraryDays` — and through it react-leaflet / hyperguest — when they
// only need the collapsed-days store.
import { useCollapsedDaysStore } from '@itineraries/widgets/itinerary-days/model/use-collapsed-days-store';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { toastStore } from '@/shared/ui';
import { useToggleLock } from '../api/use-toggle-lock';
import { ItineraryViewTabsView } from './itinerary-view-tabs-view';

type ItineraryViewTabsProps = {
  itineraryId: string | null;
  isLocked: boolean;
  // Viewing a previous version read-only — the Lock chip is a mutation, so it
  // is hidden. View switching / collapse-all stay available (read-only UI).
  isReadOnly?: boolean;
  dayIndexes: number[];
  // See `ItineraryViewTabsViewProps.chapterSelectorSlot`. Passed
  // through so the page can render the filter pill inside the chip
  // row without this component owning chapter data.
  chapterSelectorSlot?: ReactNode;
  // See `ItineraryViewTabsViewProps.daySelectorSlot` — the "View" day filter.
  daySelectorSlot?: ReactNode;
  // See `ItineraryViewTabsViewProps.bookingStatusSlot`.
  bookingStatusSlot?: ReactNode;
  // See `ItineraryViewTabsViewProps.removedServicesSlot`.
  removedServicesSlot?: ReactNode;
};

// Component layer. Owns the router search-param read/write and the
// lock-toggle mutation; hands the resolved state + callbacks down
// to `ItineraryViewTabsView`.
export const ItineraryViewTabs = ({
  itineraryId,
  isLocked,
  isReadOnly = false,
  dayIndexes,
  chapterSelectorSlot,
  daySelectorSlot,
  bookingStatusSlot,
  removedServicesSlot,
}: ItineraryViewTabsProps) => {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ItineraryDetailSearch;
  const active: ItineraryView = search.view ?? 'chapters';

  const collapsedDays = useCollapsedDaysStore((s) => s.collapsedDays);
  const collapseAll = useCollapsedDaysStore((s) => s.collapseAll);
  const expandAll = useCollapsedDaysStore((s) => s.expandAll);

  const isChapterEditorOpen = useChapterEditorStore((s) => s.isOpen);
  const openChapterEditor = useChapterEditorStore((s) => s.open);

  const allCollapsed =
    dayIndexes.length > 0 && dayIndexes.every((i) => collapsedDays.has(i));

  const toggleLockMutation = useToggleLock({ itineraryId });

  // Side-effect toasts live here (not in the api/ hook) per the
  // architecture doc. React to `data` / `error` on the mutation
  // state.
  useEffect(() => {
    const data = toggleLockMutation.data;
    if (!data) return;
    toastStore.getState().show({
      message: data.isLocked ? 'Itinerary locked' : 'Itinerary unlocked',
    });
  }, [toggleLockMutation.data]);

  useEffect(() => {
    if (!toggleLockMutation.error) return;
    toastStore.getState().show({
      message: 'Could not change lock state — please try again',
    });
  }, [toggleLockMutation.error]);

  const onToggleLock = () => {
    if (isReadOnly || !itineraryId || toggleLockMutation.isPending) return;
    toggleLockMutation.mutate({
      id: itineraryId,
      patch: { isLocked: !isLocked },
    });
  };

  const onSelectView = (next: ItineraryView) => {
    navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        // Clicking the already-active view or chapters toggles back to the
        // default (chapters = no view param).
        view: next === 'chapters' || next === active ? undefined : next,
      }),
      replace: true,
    });
  };

  return (
    <ItineraryViewTabsView
      active={active}
      allCollapsed={allCollapsed}
      bookingStatusSlot={bookingStatusSlot}
      chapterSelectorSlot={chapterSelectorSlot}
      daySelectorSlot={daySelectorSlot}
      removedServicesSlot={removedServicesSlot}
      isChapterEditorOpen={isChapterEditorOpen}
      isLocked={isLocked}
      isReadOnly={isReadOnly}
      lockDisabled={!itineraryId || toggleLockMutation.isPending}
      onCollapseAll={allCollapsed ? expandAll : () => collapseAll(dayIndexes)}
      onOpenChapterEditor={openChapterEditor}
      onSelectView={onSelectView}
      onToggleLock={onToggleLock}
    />
  );
};
